import React from "react";
import { Avatar, Box, Container, makeStyles, colors } from "@material-ui/core";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import BusinessBranch from "../../../models/BusinessBranch";
import Business from "../../../models/Business";
import { ButtonLink } from "../../../components/button-link/ButtonLink";
import { CheckInvitationCodeScreen } from "../../check-invitation-code/CheckInvitationCodeScreen";
import { CreateBusinessScreen } from "../../create-business/CreateBusinessScreen";

interface BusinessSearchResultProp {
  data: Array<BusinessBranch | Business> | null;
  query: string;
  flag?: string;
}

export const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: "block",
      width: "100%",
      textAlign: "left",
      margin: 0,
    },

    title: {
      display: "block",
      fontSize: 14,
      fontWeight: theme.typography.fontWeightMedium,
    },

    itemsContent: {
      margin: "20px 0",

      "& div.itemContent": {
        padding: "14px 0",
        Width: "100%",
        display: "flex",
        flexWrap: "nowrap",

        [theme.breakpoints.down("xs")]: {
          alignItems: "normal",
        },

        "&:hover": {
          cursor: "pointer",
        },

        "& div.contentInner": {
          position: "relative",
          paddingLeft: 22,
          display: "flex",
          width: "100%",
          justifyContent: "space-between",

          [theme.breakpoints.down("xs")]: {
            display: "block",
          },

          "& div.contentText": {
            paddingRight: 26,
            overflow: "hidden",
            width: "100%",

            "& span": {
              display: "block",
              fontSize: 15,
              fontWeight: theme.typography.fontWeightMedium,
            },

            "& p": {
              fontSize: 12,
              fontWeight: theme.typography.fontWeightLight,
              margin: 0,
              padding: 0,
              color: theme.palette.action.active,
            },
          },

          "& div.contentAction": {
            paddingTop: 10,
            [theme.breakpoints.down("xs")]: {
              marginTop: 14,
            },

            "& a": {
              padding: "6px 10px 6px 10px",
              fontSize: 13,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          },
        },
      },
    },

    noResultWrapper: {
      textAlign: "center",
      fontSize: ".8rem",
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),

      [theme.breakpoints.down("xs")]: {
        padding: 0,
      },
    },

    branchResultBottomWrapper: {
      position: "fixed",
      bottom: 10,
      left: 0,

      fontSize: ".8rem",

      "& p": {
        color: `${colors.grey[600]} !important`,
      },

      "& a": {
        display: "inline-block",
        color: `${colors.blue[500]} !important`,
        textDecoration: "underline",
      },

      "& .branchResultBottomWrapperInner": {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),

        [theme.breakpoints.down("xs")]: {
          padding: 0,
        },

        "& a": {
          display: "inline-block",
          marginLeft: "4px",
        },
      },
    },
  }),
  { name: "branch-search-result-style", flip: null }
);

export const BusinessSearchResult = (props: BusinessSearchResultProp) => {
  const intl = useIntl();
  const classes = useStyles(props);
  const { data, query, flag } = props;

  function renderBusinessBranch(item: BusinessBranch, key: string) {
    return (
      <div className="itemContent" key={key}>
        <div className="avatar">
          <Avatar sizes="100" variant="rounded" src={item.avatar()} />
        </div>
        <div className="contentInner">
          <div className="contentText">
            <span>{item.fullName()}</span>
            <p>{item.address}</p>
          </div>
          <div className="contentAction">
            {!item.hasAManager() ? (
              <ButtonLink
                text={intl.formatMessage({ id: "sharedScreen.selectBranch" })}
                to={`${CheckInvitationCodeScreen.routeName}?id=${item.id}`}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderBusinessBranches() {
    return (
      <>
        <Box component="span" className={classes.title}>
          <FormattedMessage
            id="sharedScreen.branchSearchResultMessage"
            values={{ count: data.length }}
          />
        </Box>
        <div className={classes.itemsContent}>
          {data.map((item, index) => {
            return renderBusinessBranch(
              item as BusinessBranch,
              `${"branch-result-item-" + index}`
            );
          })}
        </div>
      </>
    );
  }

  function renderBusinesses() {
    return (
      <Box
        mt={2}
        display="block"
        component="span"
        className={classes.noResultWrapper}
      >
        <FormattedMessage id="sharedScreen.businessExistWithSameName" />
      </Box>
    );
  }

  function renderNoResultsSearch() {
    return (
      <>
        <Container
          maxWidth="xs"
          disableGutters
          className={classes.noResultWrapper}
        >
          <Box component="span" display="block">
            <FormattedMessage
              id="sharedScreen.branchSearchResultSubMessage"
              values={{ query: query }}
            />
          </Box>
          <Box component="div" mt={4}>
            {flag === "bo" ? (
              <ButtonLink
                to={`${CreateBusinessScreen.routeName}?name=${encodeURIComponent(query)}`}
                text={intl.formatMessage({
                  id: "sharedScreen.createNewBusiness",
                })}
              />
            ) : (
              <></>
            )}
          </Box>
        </Container>
        <Container maxWidth="xl" className={classes.branchResultBottomWrapper}>
          <Container maxWidth="xs" className="branchResultBottomWrapperInner">
            <Box component="p" textAlign="center">
              <FormattedMessage id="sharedScreen.branchSearchResultExtraMessage" />
              <Link to="#">help@icip.com</Link>
            </Box>
          </Container>
        </Container>
      </>
    );
  }

  function render() {
    if (data !== null) {
      if (flag === "bo") {
        return data.length === 0 ? renderNoResultsSearch() : renderBusinesses();
      }

      return data.length === 0
        ? renderNoResultsSearch()
        : renderBusinessBranches();
    }

    return <></>;
  }

  return <div className={classes.root}>{render()}</div>;
};
